import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Il pulsante che cambia la foto di una persona, di un mezzo o di un articolo.
 *
 * Da telefono e da tablet si fa al volo: «Scatta una foto» apre direttamente
 * la fotocamera, «Scegli dal dispositivo» apre la galleria. Da computer la
 * fotocamera di solito non c'e' e si usa la seconda voce.
 *
 * Perche' non e' un semplice input nascosto come altrove: su tablet il
 * selettore dei file aperto da codice (`input.click()` su un campo
 * `display:none`) non si apre sempre. Qui il campo e' dentro una <label>, e il
 * tocco arriva al campo vero senza passare da JavaScript: e' l'unico modo che
 * funziona su tutti e tre i sistemi.
 *
 * L'immagine viene convertita in JPEG dal browser prima di partire. Serve per
 * due ragioni: le foto dell'iPad escono in HEIC, che il server non sa aprire e
 * che poi non si vede ne' nell'elenco ne' sul tesserino; e una foto da 8 MB
 * sotto rete mobile non arriva.
 */
export default function BottoneFoto({
  url, onCaricata, etichetta = "", className = "", classeContenitore = "",
  testId, titolo = "Cambia la foto", children,
}) {
  const [aperto, setAperto] = useState(false);
  const [invio, setInvio] = useState(false);

  const manda = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";          // cosi' la stessa foto si puo' riprovare
    setAperto(false);
    if (!file) return;
    setInvio(true);
    try {
      const { dati, nome } = await aJpeg(file);
      const fd = new FormData();
      fd.append("file", dati, nome);
      await api.post(url, fd);
      toast.success("Foto aggiornata");
      onCaricata?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Foto non caricata");
    } finally {
      setInvio(false);
    }
  };

  const voce = "flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left " +
               "cursor-pointer hover:bg-slate-50 active:bg-slate-100";

  return (
    <div className={`relative inline-block ${classeContenitore}`}>
      {children ? (
        <button type="button" onClick={() => setAperto((a) => !a)} title={titolo}
                data-testid={testId} className="relative block">
          {children}
          <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0067b2]
                           text-white flex items-center justify-center shadow">
            {invio ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                   : <Camera className="w-3.5 h-3.5" />}
          </span>
        </button>
      ) : (
        <Button type="button" variant="outline" size="sm" title={titolo}
                data-testid={testId} className={className} disabled={invio}
                onClick={() => setAperto((a) => !a)}>
          {invio ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          {etichetta && <span className="ml-1">{etichetta}</span>}
        </Button>
      )}

      {aperto && (<>
        {/* Un tocco fuori chiude il menu. */}
        <div className="fixed inset-0 z-40" onClick={() => setAperto(false)} />
        <div className="absolute z-50 mt-1 left-0 w-56 rounded-lg border border-slate-200
                        bg-white shadow-lg overflow-hidden"
             style={{ pointerEvents: "auto" }}>
          {/* `capture` dice al tablet di aprire la fotocamera invece della
              galleria. Il computer lo ignora e apre comunque i file. */}
          <label className={voce} data-testid="foto-scatta">
            <Camera className="w-4 h-4 text-[#0067b2]" /> Scatta una foto
            <input type="file" accept="image/*" capture="environment"
                   className="sr-only" onChange={manda} />
          </label>
          <label className={`${voce} border-t border-slate-100`} data-testid="foto-scegli">
            <ImagePlus className="w-4 h-4 text-slate-500" /> Scegli dal dispositivo
            <input type="file" accept="image/*"
                   className="sr-only" onChange={manda} />
          </label>
        </div>
      </>)}
    </div>
  );
}

const LATO_MAX = 1600;

/**
 * Riduce l'immagine e la riscrive in JPEG, dentro il browser.
 *
 * Se il browser non riesce ad aprirla la lascia com'e': meglio tentare il
 * caricamento che bloccare la persona davanti al tablet. Il server, se non la
 * riconosce nemmeno lui, lo dice con un messaggio chiaro.
 */
async function aJpeg(file) {
  // Un PNG leggero si lascia com'e': passando dalla tela perderebbe la
  // trasparenza, e sulle foto degli articoli si vedrebbe il fondo nero.
  if (file.type === "image/png" && file.size <= 2 * 1024 * 1024) {
    return { dati: file, nome: file.name || "foto.png" };
  }
  try {
    const sorgente = await apriImmagine(file);
    const larga = sorgente.naturalWidth || sorgente.width;
    const alta = sorgente.naturalHeight || sorgente.height;
    if (!larga || !alta) throw new Error("immagine vuota");
    const scala = Math.min(1, LATO_MAX / Math.max(larga, alta));
    const tela = document.createElement("canvas");
    tela.width = Math.max(1, Math.round(larga * scala));
    tela.height = Math.max(1, Math.round(alta * scala));
    tela.getContext("2d").drawImage(sorgente, 0, 0, tela.width, tela.height);
    sorgente.close?.();
    const blob = await new Promise((ok) => tela.toBlob(ok, "image/jpeg", 0.88));
    if (!blob) throw new Error("conversione non riuscita");
    return { dati: blob, nome: "foto.jpg" };
  } catch {
    return { dati: file, nome: file.name || "foto.jpg" };
  }
}

function apriImmagine(file) {
  if (window.createImageBitmap) {
    // `from-image` applica la rotazione scritta dentro la foto: senza, i
    // ritratti scattati col tablet in mano arrivano coricati.
    return window.createImageBitmap(file, { imageOrientation: "from-image" })
      .catch(() => conTag(file));
  }
  return conTag(file);
}

function conTag(file) {
  return new Promise((risolvi, rifiuta) => {
    const indirizzo = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(indirizzo); risolvi(img); };
    img.onerror = () => { URL.revokeObjectURL(indirizzo); rifiuta(new Error("non leggibile")); };
    img.src = indirizzo;
  });
}
